import React, { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { useTimModalStore } from './useTimModalStore';
import type { DispatchMessage, SetStatePayload } from './tim-bridge';
import appConfig from '../utils/config';

const log = (msg: string, ...args: unknown[]) =>
  console.log(`[IM-BRIDGE] ${msg}`, ...args);

const TIM_BASE_URL = appConfig.timBaseUrl.replace(/\/$/, '');

export interface ModalShellProps {
  open: boolean;
  mounted: boolean;
  onClose: () => void;
  closeIcon: React.ReactNode;
  children?: React.ReactNode;
}

interface TimModalProps {
  role: 'brand' | 'creator';
  platform: 'pc' | 'h5';
  ModalShell: React.ComponentType<ModalShellProps>;
  closeIcon: React.ReactNode;
}

/**
 * 核心 IM 弹窗组件，两端代码完全一致，差异全部通过 props 注入。
 *
 * iframe src 只含 platform、lang，不含 token 和 role。
 * 鉴权通过 TIM_SET_STATE AUTH 发送，src 永远稳定，HMR 不白屏。
 */
const TimModal: React.FC<TimModalProps> = ({
  role,
  platform,
  ModalShell,
  closeIcon
}) => {
  const { getToken } = useAuth();
  const { i18n } = useTranslation();
  const {
    showTimModal,
    currentCampaignId,
    currentOrderId,
    timLoaded,
    timUrl,
    setShowTimModal,
    setTimReady,
    setTimUnreadCount
  } = useTimModalStore();

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const targetOriginRef = useRef(new URL(TIM_BASE_URL).origin);
  const imReadyRef = useRef(false);
  const pendingNavRef = useRef<{
    orderId?: string;
    campaignId?: string;
  } | null>(null);
  const lastDeliveredNavKeyRef = useRef('');

  // ── postMessage 发送函数 ──────────────────────────────────────────

  const sendSetState = useCallback((payload: SetStatePayload) => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    log('→ iframe TIM_SET_STATE', payload.type, payload);
    win.postMessage(
      { type: 'TIM_SET_STATE', payload },
      targetOriginRef.current
    );
  }, []);

  /** orderId 有值 → 导航到具体会话；无值 → 刷新全局列表 */
  const sendOpen = useCallback(
    (orderId?: string, campaignId?: string) => {
      if (orderId)
        lastDeliveredNavKeyRef.current = `${orderId}::${campaignId || ''}`;
      sendSetState({ type: 'OPEN', orderId, campaignId });
    },
    [sendSetState]
  );

  const sendLang = useCallback(
    (lang: string) => sendSetState({ type: 'LANG', lang }),
    [sendSetState]
  );

  const sendImVisible = useCallback(
    (visible: boolean) => sendSetState({ type: 'IM_VISIBLE', visible }),
    [sendSetState]
  );

  const shouldSkipNavigate = useCallback(
    (orderId: string, campaignId?: string) => {
      return (
        `${orderId}::${campaignId || ''}` === lastDeliveredNavKeyRef.current
      );
    },
    []
  );

  // ── Clerk token 获取 ──────────────────────────────────────────────
  // 每次 READY 都调用 getToken() 取最新 token，避免过期的旧 token 被回传

  const getImToken = useCallback(async () => {
    return getToken({ template: 'testing-template' }).catch(() =>
      getToken().catch(() => null)
    );
  }, [getToken]);

  // ── 监听 im-ui 发来的 TIM_DISPATCH ─────────────────────────────────

  useEffect(() => {
    const origin = targetOriginRef.current;

    const handler = async (event: MessageEvent) => {
      if (event.origin !== origin) return;
      const data = event.data as DispatchMessage | undefined;
      if (data?.type !== 'TIM_DISPATCH') return;

      const { payload } = data;
      log('← iframe TIM_DISPATCH', payload.type, payload);

      switch (payload.type) {
        case 'READY': {
          const token = await getImToken();
          if (!token) {
            console.error(
              '[IM-BRIDGE] READY received but failed to get Clerk token'
            );
            return;
          }
          sendSetState({
            type: 'AUTH',
            token,
            role,
            platform,
            lang: i18n.language || 'en-US'
          });
          if (showTimModal) sendImVisible(true);
          imReadyRef.current = true;

          if (pendingNavRef.current) {
            const { orderId, campaignId } = pendingNavRef.current;
            pendingNavRef.current = null;
            if (!orderId || !shouldSkipNavigate(orderId, campaignId)) {
              sendOpen(orderId, campaignId);
            }
          } else if (currentOrderId) {
            if (
              !shouldSkipNavigate(
                currentOrderId,
                currentCampaignId || undefined
              )
            ) {
              sendOpen(currentOrderId, currentCampaignId || undefined);
            }
          } else {
            sendOpen();
          }
          break;
        }
        case 'UNREAD_COUNT':
          setTimUnreadCount(Number(payload.count) || 0);
          break;
        case 'CLOSE':
          setShowTimModal(false);
          break;
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [
    getImToken,
    role,
    currentOrderId,
    currentCampaignId,
    sendSetState,
    sendLang,
    sendOpen,
    shouldSkipNavigate,
    setTimUnreadCount,
    i18n.language,
    showTimModal,
    sendImVisible
  ]);

  // ── 挂载 iframe（裸 URL，无任何参数）────────────────────────────

  const doInitTim = useCallback(() => {
    if (timLoaded) return;
    log('mounting iframe, src:', `${TIM_BASE_URL}/`);
    setTimReady(`${TIM_BASE_URL}/`);
  }, [timLoaded, setTimReady]);

  useEffect(() => {
    if (!showTimModal || timLoaded) return;
    doInitTim();
  }, [showTimModal, timLoaded, doInitTim]);

  // ── 弹窗打开时发送导航 ────────────────────────────────────────────

  useEffect(() => {
    if (!showTimModal || !currentOrderId) return;
    if (shouldSkipNavigate(currentOrderId, currentCampaignId || undefined))
      return;
    if (imReadyRef.current) {
      sendOpen(currentOrderId, currentCampaignId || undefined);
    } else {
      pendingNavRef.current = {
        orderId: currentOrderId,
        campaignId: currentCampaignId || undefined
      };
    }
  }, [
    showTimModal,
    currentOrderId,
    currentCampaignId,
    sendOpen,
    shouldSkipNavigate
  ]);

  useEffect(() => {
    if (!showTimModal || currentOrderId) return;
    if (imReadyRef.current) sendOpen();
  }, [showTimModal, currentOrderId, sendOpen]);

  useEffect(() => {
    if (!imReadyRef.current) return;
    sendLang(i18n.language || 'en-US');
  }, [i18n.language, sendLang]);

  useEffect(() => {
    if (!timLoaded || !imReadyRef.current) return;
    sendImVisible(showTimModal);
  }, [timLoaded, showTimModal, sendImVisible]);

  return (
    <ModalShell
      open={showTimModal}
      mounted={timLoaded}
      onClose={() => setShowTimModal(false)}
      closeIcon={closeIcon}
    >
      {timLoaded ? (
        <iframe
          ref={iframeRef}
          title="TIM"
          src={timUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block'
          }}
        />
      ) : null}
    </ModalShell>
  );
};

export default TimModal;
